import apiClient, { loadFileAsBlob, loadFileAsBase64, downloadFile } from '../../../../../../services/apiClient';

import { sendFileAsBinaryPOST } from '../../../../../../services/saveFile';
import { getSpecializations } from '../../GroupDetail/actions';


const ret_file_desc = (files)=> files.map(cert=>({_id: cert._id, name: cert.file.name, size: cert.file.size || 0, type: cert.file.mime}));

// Основные API endpoints
export const childApiEndpoints = {
  getChild: (childId) => `children/${childId}`,
  updateChild: ({kg_id, gr_id, childId}) => `kindergartens/${kg_id}/groups/${gr_id}/child/${childId}`,
  uploadFile: `files/upload`,
  getHistory: (childId) => `children/${childId}/statuses`,
  getAccruals: (childId) => `children/${childId}/accruals`,
  getAttendance: (childId) => `children/${childId}/attendance`,
  getPayments: (childId) => `children/${childId}/payments`,
  getBenefits: ()=>`benefits`,
  getSpecializations: ()=>`specializations`
};

// Кэш для справочников
let benefitsCache = null;
let specializationsCache = null;

// Функции для работы со справочниками
export const dictionaryService = {
  getBenefits: async () => {
    if (benefitsCache) return benefitsCache;
    
    try {
      const data = await apiClient.get(childApiEndpoints.getBenefits());
      benefitsCache = data.data || [];
      return benefitsCache;
    } catch (error) {
      console.error('Error fetching benefits:', error);
      return [];
    }
  },

  getSpecializations: async () => {
    if (specializationsCache) return specializationsCache;
    
    try {
      const data = await apiClient.get(childApiEndpoints.getSpecializations());
      specializationsCache = data.data || [];
      return specializationsCache;
    } catch (error) {
      console.error('Error fetching specializations:', error);
      return [];
    }
  },

  getBenefitName: async (benefitId) => {
    const benefits = await dictionaryService.getBenefits();
    const benefit = benefits.find(b => b._id === benefitId);
    return benefit?.name || benefitId;
  },

  getSpecializationName: async (specializationId) => {
    const specializations = await dictionaryService.getSpecializations();
    const specialization = specializations.find(s => s._id === specializationId);
    return specialization?.name || specializationId;
  },

  clearCache: () => {
    benefitsCache = null;
    specializationsCache = null;
  }
};

// Трансформация файлов для FileUploader
export const transformFilesForUploader = (files, section = '') => {
  if (!files || !Array.isArray(files)) return [];
  
  return files.map(file => {
    const fileObj = {
      name: file.file?.name || file.name || 'Файл',
      size: file.file?.size || file.size || 0,
      type: file.file?.type || file.type || 'application/octet-stream',
      file: file,
      _id: file._id || file.file?._id,
      section: section // Добавляем информацию о секции
    };
    
    return fileObj;
  });
};

// Трансформация файлов из FileUploader для бэкенда
export const transformFilesForBackend = (uploaderFiles, originalFiles = []) => {
  return uploaderFiles.map(uploaderFile => {
    // Если файл новый (base64)
    if (uploaderFile.file && typeof uploaderFile.file === 'string' && uploaderFile.file.startsWith('data:')) {
      return {
        type: "specializations", // будет переопределено в зависимости от контекста
        file: {
          name: uploaderFile.name,
          mime: uploaderFile.type,
          size: uploaderFile.size,
          datetimeupload: new Date(),
          date: new Date(),
          storage: {
            path: "temp",
            name: `temp_${Date.now()}_${uploaderFile.name}`
          },
          securuty: {
            ro: false
          }
        }
      };
    }
    
    // Если файл уже существующий
    return uploaderFile.file || uploaderFile;
  });
};

// Трансформация данных из бэкенда во фронт
export const transformBackendToFrontend = async (backendData) => {
  const child = backendData;
  
  // Находим текущий статус
  const currentStatus = child.statuses?.find(status => 
    status.state === 'active' || !status.dateTo || new Date(status.dateTo) > new Date()
  );
  
  // Находим родителей в relationFamily
  const father = child.relationFamily?.find(person => 
    person.relationDegree === 'батько'
  );

  const mother = child.relationFamily?.find(person => 
    person.relationDegree === 'мати'
  );

  // Получаем названия льгот и специализаций
  const benefitName = child.benefits?.[0]?.benefitId ? 
    await dictionaryService.getBenefitName(child.benefits[0].benefitId) : '';
  
  const specializationName = child.illnesses?.[0] ? 
    await dictionaryService.getSpecializationName(child.illnesses[0]) : '';

  return {
    // Basic info
    firstName: child.firstName || '',
    lastName: child.lastName || '',
    middleName: child.middleName || '',
    fullName: `${child.lastName} ${child.firstName} ${child.middleName || ''}`.trim(),
    birthDate: child.birthDate,
    sex: child.sex || '',
    fileNumber: currentStatus?.smallNumber || child.childCode,
    id: child._id,
    admissionDate: child.currentKindergarten?.dateFrom?.split("T")[0] || '',
    
    // Birth certificate
    birthCertificateNumber: child.birthCertificate?.number || '',
    birthCertificateDate: child.birthCertificate?.issueDate?.split("T")[0] || '',
    birthCertificateCountry: child.birthCertificate?.issueCountry || 'Україна',
    
    // Status
    residenceVerificationDate: child.statusMR?.date,
    
    // Health information
    healthCondition: child.illnesses?.join(', ') || '',
    healthSpecialization: specializationName,
    healthDocument: child.medicalProtocolType,
    healthDocumentDate: child.medicalProtocolDate?.split("T")[0] || '',
    healthDocumentNumber: child.medicalProtocolNumber,
    healthDocumentIssuedBy: child.medicalProtocolIssueTo,
    healthFiles: transformFilesForUploader(child.medicalScan),
    illnesses: child.illnesses || [],
    
    // Benefits information
    benefitDescription: benefitName,
    benefitId: child.benefits?.[0]?.benefitId || '',
    benefitDocument: child.benefits?.[0]?.documentType || '',
    benefitDocumentDate: child.benefits?.[0]?.documentIssueDate || '',
    benefitDocumentNumber: child.benefits?.[0]?.documentNumber || '',
    benefitDocumentIssuedBy: child.benefits?.[0]?.documentTo || '',
    benefitFiles: transformFilesForUploader(child.scanBenefits),
    
    // Contacts - РАЗДЕЛЬНЫЕ ПОЛЯ ДЛЯ РОДИТЕЛЕЙ
    motherLastName: mother?.lastName || '',
    motherFirstName: mother?.firstName || '',
    motherMiddleName: mother?.middleName || '',
    motherPhone: mother?.contacts?.[0]?.phone || '',
    motherEmail: mother?.contacts?.[0]?.email || '',
    motherIPN: mother?.code || '',
    motherCitizenship: mother?.sitizen || 'Україна',
    motherAddress: mother?.contacts?.[0]?.address || '',
    motherViber: mother?.contacts?.[0]?.viber || '',
    
    fatherLastName: father?.lastName || '',
    fatherFirstName: father?.firstName || '',
    fatherMiddleName: father?.middleName || '',
    fatherPhone: father?.contacts?.[0]?.phone || '',
    fatherEmail: father?.contacts?.[0]?.email || '',
    fatherIPN: father?.code || '',
    fatherCitizenship: father?.sitizen || 'Україна',
    fatherAddress: father?.contacts?.[0]?.address || '',
    fatherViber: father?.contacts?.[0]?.viber || '',
    
    // Additional info
    isOrphan: child.social?.orphan || false,
    isDisplaced: !!child.social?.displacedType,
    isDistanceLearning: child.social?.onlineLearning || false,
    isContactLost: child.social?.lostContact || false,
    foodType: child.kitchenBen?.name || 'Звичайне',
    
    // Related persons (все кроме родителей)
    relatedPersons: buildRelatedPersons(child),
        // Health information
    healthFiles: child.medicalScan && ret_file_desc(child.medicalScan) || [],
    
    // Benefits information  
    benefitFiles: child.scanBenefits && ret_file_desc(child.scanBenefits) || [],
    
    // Birth certificate files
    birthCertificateFiles: child.birthCertificate?.scanCert && ret_file_desc(child.birthCertificate?.scanCert) || [], //transformFilesForUploader(child.birthCertificate?.scanCert, 'certificate'),
    
    // Tab data (will be populated separately)
    history: [],
    accruals: [],
    attendance: [],
    payments: []
  };
};

// Трансформация данных из фронта в бэкенд
export const transformFrontendToBackend = (frontendData, originalData) => {
  return {
    // Basic info
    childFirstName: frontendData.firstName,
    childMiddleName: frontendData.middleName,
    childLastName: frontendData.lastName,
    sex: frontendData.sex,
    birthDate: frontendData.birthDate,
    
    // Birth certificate
    birthCertificate: {
      number: frontendData.birthCertificateNumber,
      issueDate: frontendData.birthCertificateDate,
      issueCountry: frontendData.birthCertificateCountry || 'Україна'
    },
    
    // Health information
    medicalProtocolNumber: frontendData.healthDocumentNumber,
    medicalProtocolDate: frontendData.healthDocumentDate,
    medicalProtocolType: frontendData.healthDocument,
    medicalProtocolIssueTo: frontendData.healthDocumentIssuedBy,
    illnesses: frontendData.illnesses || [],
    medicalScan: transformFilesForBackend(frontendData.healthFiles || [], originalData.medicalScan),
    
    // Benefits
    benefits: frontendData.benefitId ? [{
      benefitId: frontendData.benefitId,
      documentNumber: frontendData.benefitDocumentNumber,
      documentIssueDate: frontendData.benefitDocumentDate,
      documentType: frontendData.benefitDocument,
      documentTo: frontendData.benefitDocumentIssuedBy
    }] : [],

    scanBenefits: transformFilesForBackend(frontendData.benefitFiles || [], originalData.scanBenefits),
    
    // Social info
    social: {
      orphan: frontendData.isOrphan,
      displacedType: frontendData.isDisplaced ? 'internal' : undefined,
      onlineLearning: frontendData.isDistanceLearning,
      lostContact: frontendData.isContactLost,
      ...originalData.social
    },

    typeKitchen: frontendData.foodType !== "Звичайне" ? "diet": "standart",
    
    // Kitchen benefits
    kitchenBen: frontendData.foodType !== 'Звичайне' ? {
      name: frontendData.foodType,
      type: 'diet',
      value: frontendData.foodType
    } : undefined,
    
    // Обновляем relationFamily с родителями и связанными лицами
    relationFamily: buildRelationFamily(frontendData, originalData)
  };
};

// Вспомогательные функции
const buildRelatedPersons = (child) => {
  const persons = [];

  // Члены семьи (исключаем родителей, т.к. они отображаются отдельно)
  if (child.relationFamily) {
    child.relationFamily.forEach(person => {
      if (!['батько', 'мати'].includes(person.relationDegree)) {
        persons.push({
          lastName: person.lastName || '',
          firstName: person.firstName || '',
          middleName: person.middleName || '',
          taxNumber: person.code || '',
          phone: person.contacts?.[0]?.phone || '',
          email: person.contacts?.[0]?.email || '',
          citizenship: person.sitizen || 'Україна',
          relationshipDegree: person.relationDegree,
          address: person.contacts?.[0]?.address || '',
          viber: person.contacts?.[0]?.viber || ''
        });
      }
    });
  }

  return persons;
};

const buildRelationFamily = (frontendData, originalData) => {
  const relationFamily = [];
  
  // Добавляем мать
  if (frontendData.motherLastName || frontendData.motherFirstName) {
    relationFamily.push({
      relationDegree: 'мати',
      firstName: frontendData.motherFirstName || '',
      lastName: frontendData.motherLastName || '',
      middleName: frontendData.motherMiddleName || '',
      code: frontendData.motherIPN || '',
      sitizen: frontendData.motherCitizenship || 'Україна',
      statusInsertDate: new Date(),
      contacts: [{
        phone: frontendData.motherPhone || '',
        email: frontendData.motherEmail || '',
        address: frontendData.motherAddress || '',
        viber: frontendData.motherViber || ''
      }],
      benefits: getMotherBenefits(originalData),
      statusMR: getMotherStatusMR(originalData)
    });
  }
  
  // Добавляем отца
  if (frontendData.fatherLastName || frontendData.fatherFirstName) {
    relationFamily.push({
      relationDegree: 'батько',
      firstName: frontendData.fatherFirstName || '',
      lastName: frontendData.fatherLastName || '',
      middleName: frontendData.fatherMiddleName || '',
      code: frontendData.fatherIPN || '',
      sitizen: frontendData.fatherCitizenship || 'Україна',
      statusInsertDate: new Date(),
      contacts: [{
        phone: frontendData.fatherPhone || '',
        email: frontendData.fatherEmail || '',
        address: frontendData.fatherAddress || '',
        viber: frontendData.fatherViber || ''
      }],
      benefits: getFatherBenefits(originalData),
      statusMR: getFatherStatusMR(originalData)
    });
  }
  
  // Добавляем связанные лица
  if (frontendData.relatedPersons && frontendData.relatedPersons.length > 0) {
    frontendData.relatedPersons.forEach(person => {
      if (person.lastName || person.firstName) {
        relationFamily.push({
          relationDegree: person.relationshipDegree || 'інші',
          firstName: person.firstName || '',
          lastName: person.lastName || '',
          middleName: person.middleName || '',
          code: person.taxNumber || '',
          sitizen: person.citizenship || 'Україна',
          statusInsertDate: new Date(),
          contacts: [{
            phone: person.phone || '',
            email: person.email || '',
            address: person.address || '',
            viber: person.viber || ''
          }],
          benefits: [],
          statusMR: {}
        });
      }
    });
  }
  
  return relationFamily;
};

// Вспомогательные функции для получения данных льгот и статусов из оригинальных данных
const getMotherBenefits = (originalData) => {
  const mother = originalData.relationFamily?.find(person => person.relationDegree === 'мати');
  return mother?.benefits || [];
};

const getFatherBenefits = (originalData) => {
  const father = originalData.relationFamily?.find(person => person.relationDegree === 'батько');
  return father?.benefits || [];
};

const getMotherStatusMR = (originalData) => {
  const mother = originalData.relationFamily?.find(person => person.relationDegree === 'мати');
  return mother?.statusMR || {};
};

const getFatherStatusMR = (originalData) => {
  const father = originalData.relationFamily?.find(person => person.relationDegree === 'батько');
  return father?.statusMR || {};
};

// API функции
export const childApiService = {
  // Получить данные ребенка
  getChild: async (childId) => {
    const response = await apiClient.get(childApiEndpoints.getChild(childId));
    return transformBackendToFrontend(response.data[0]);
  },

  // Обновить данные ребенка
  updateChild: async ({kg_id, gr_id, childId, data}) => {
    const upd_info = await apiClient.put(childApiEndpoints.updateChild({kg_id, gr_id, childId}), data);
    return upd_info;
  },

  // Загрузить файл
  uploadFile: async (childId, file, fieldName) => {
    try {
      const result = await sendFileAsBinaryPOST({
        child_id: childId,
        file: file,
        typedoc: "children",
        part: fieldName
      });
      return result;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  // Получить историю
  getHistory: async (childId) => {
    return [];
  },

  // Получить начисления
  getAccruals: async (childId) => {
    return [];
  },

  // Получить посещения
  getAttendance: async (childId) => {
   return [];
  },

  // Получить платежи
  getPayments: async (childId) => {
    return [];
  }
}

export const childTabDataService = {
  // Получить историю статусов
  getChildHistory: async (childId) => {
    try {
      const response = await apiClient.get(childApiEndpoints.getHistory(childId));
      const childData = response.data;
      return childData;
    } catch (error) {
      console.error('Error loading child history:', error);
      return [];
    }
  },

  // Получить начисления
  getChildBilling: async (childId, kgId) => {
    try {
      const response = await apiClient.get(`get_ch_billing?id_ch=${childId}&kg_id=${kgId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error loading child billing:', error);
      return [];
    }
  },

  // Получить посещения
  getChildAttendance: async (childId) => {
    try {
      const now = new Date();
      const firstDayOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01T00:00`;
      const response = await apiClient.get(`get_attends_parent?id_ch=${childId}&date_from=${firstDayOfMonth}`);
      return response.data || [];
    } catch (error) {
      console.error('Error loading child attendance:', error);
      return [];
    }
  },

  // Получить оплаты
  getChildPayments: async (childId, kgId) => {
    try {
      const response = await apiClient.get(`get_ch_payments?id_ch=${childId}&kg_id=${kgId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error loading child payments:', error);
      return [];
    }
  }
};

// Добавить в childApiService.js

// Функции для работы с файлами
export const fileService = {
  // Загрузить файл
  uploadFile: async (childId, file, part) => {
    try {
      const result = await sendFileAsBinaryPOST({
        _id: childId,
        file: file,
        typedoc: "children", 
        part: part
      });
      return result;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  // Удалить файл
  deleteFile: async (childId, fileId, part) => {
    try {
      const response = await apiClient.delete(`children/${childId}/file/${fileId}/${part}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  },

  // Получить файл для просмотра
  getFile: async (childId, fileId, part, mime) => {
    try {
      const response = await loadFileAsBase64(`children/${childId}/file/${fileId}/${part}`, mime);
      return response;
    } catch (error) {
      console.error('Error loading file:', error);
      throw error;
    }
  },
  downloadFile: async (childId, fileId, part, name)=>{
    try{
      const response = await downloadFile(`children/${childId}/file/${fileId}/${part}`, name);
      return ({ result: 'success' });
    } catch (error) {
      console.error('Error loading file:', error);
      throw error;
    }
  }
};